import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FaSearch,
  FaFileAlt,
  FaDownload,
  FaBookmark,
  FaRegBookmark,
  FaThList,
  FaBrain,
  FaChartBar,
  FaUsers,
  FaPlus,
  FaFileCode,
  FaPrint,
  FaCheck,
  FaEdit,
} from 'react-icons/fa';
import {
  TechShell,
  TechPageHeader,
  TechSection,
  TechTabs,
  TechSelect,
  TechInput,
  TechButton,
  TechButtonGhost,
  TechChip,
  StatusPill,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';

import ResearchCharts from './ResearchCharts';
import ResearchInsights from './ResearchInsights';
import ResearchViewer from './ResearchViewer';
import ResearchEditor from './ResearchEditor';
import { RESEARCH_ITEMS, CATEGORY_META } from './data';
import { downloadMarkdown, downloadJSON, printItem } from './download';
import { listResearch } from '../../services/researchService';
import CronCountdown from '../common/CronCountdown';

const CUSTOM_KEY = 'arcana_research_custom_v1';

function loadCustom() {
  try {
    const raw = localStorage.getItem(CUSTOM_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}
function persistCustom(list) {
  try { localStorage.setItem(CUSTOM_KEY, JSON.stringify(list)); } catch {}
}
function nextId(existing) {
  const max = existing.reduce((acc, it) => Math.max(acc, Number(it.id) || 0), 0);
  return max + 1;
}

/* ---------- styled ---------- */

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
  padding: 0.85rem 0.95rem;
  background: rgba(36, 52, 77, 0.55);
  border: 1px solid ${borderSoft};
  border-radius: 8px;
  margin-bottom: 1.25rem;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;

  label {
    font-family: ${mono};
    font-size: 0.6rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--text-disabled);
    display: flex;
    align-items: center;
    gap: 4px;
  }
  svg { color: ${accent}; font-size: 0.75rem; }
`;

const SearchWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 240px;

  > svg {
    position: absolute;
    left: 0.7rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-disabled);
    font-size: 0.85rem;
  }
  input { padding-left: 2rem; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(310px, 1fr));
  gap: 1rem;
`;

const Card = styled.article`
  position: relative;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.012), transparent 35%),
    var(--color-tertiary);
  border: 1px solid ${borderSoft};
  border-left: 3px solid ${({ accentColor }) => accentColor || accent};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: border-color 0.18s ease, transform 0.18s ease;

  &:hover {
    border-color: rgba(0, 180, 216, 0.3);
    transform: translateY(-2px);
  }

  &::before, &::after {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    border-color: ${accent};
    border-style: solid;
    border-width: 0;
    pointer-events: none;
  }
  &::before { top: -1px; left: -1px; border-top-width: 1px; border-left-width: 1px; }
  &::after { bottom: -1px; right: -1px; border-bottom-width: 1px; border-right-width: 1px; }
`;

const CardHead = styled.div`
  padding: 0.95rem 1.1rem;
  display: flex;
  justify-content: space-between;
  gap: 0.55rem;
  border-bottom: 1px solid ${borderSoft};
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 0.96rem;
  color: var(--text-primary);
  flex: 1;
  line-height: 1.35;
`;

const CardBody = styled.div`
  padding: 0.85rem 1.1rem;
  flex: 1;
`;

const CardText = styled.p`
  margin: 0 0 0.6rem;
  color: var(--text-secondary);
  font-size: 0.86rem;
  line-height: 1.5;
`;

const CardMeta = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: ${mono};
  font-size: 0.65rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-disabled);
  margin-bottom: 0.45rem;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const Tag = styled.span`
  font-family: ${mono};
  font-size: 0.66rem;
  color: #cfe9f4;
  background: rgba(0, 180, 216, 0.06);
  border: 1px solid rgba(0, 180, 216, 0.22);
  border-radius: 3px;
  padding: 1px 6px;

  &::before { content: '#'; color: ${accent}; opacity: 0.85; }
`;

const CardActions = styled.div`
  display: flex;
  border-top: 1px solid ${borderSoft};
  background: ${panelDeep};
`;

const ActionBtn = styled.button`
  flex: 1;
  background: transparent;
  border: none;
  padding: 0.55rem 0.4rem;
  color: var(--text-disabled);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  font-family: inherit;
  font-size: 0.74rem;
  letter-spacing: 0.04em;
  border-right: 1px solid ${borderSoft};
  transition: color 0.18s ease, background 0.18s ease;

  &:last-child { border-right: none; }
  &:hover, &.active { color: ${accent}; background: rgba(0, 180, 216, 0.05); }
  svg { font-size: 0.85rem; }
`;

const DownloadWrap = styled.div`
  flex: 1;
  position: relative;
  border-right: 1px solid ${borderSoft};

  &:last-child { border-right: none; }
`;

const DownloadMenu = styled.div`
  position: absolute;
  bottom: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  min-width: 170px;
  background: var(--color-tertiary);
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  z-index: 10;
  overflow: hidden;

  button {
    background: transparent;
    border: none;
    padding: 0.55rem 0.85rem;
    color: var(--text-secondary);
    text-align: left;
    cursor: pointer;
    font-family: inherit;
    font-size: 0.84rem;
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
    transition: color 0.15s ease, background 0.15s ease;

    svg { font-size: 0.85rem; color: ${accent}; }

    &:hover {
      color: var(--text-primary);
      background: rgba(255, 255, 255, 0.04);
    }

    & + button { border-top: 1px solid ${borderSoft}; }
  }
`;

const Toast = styled.div`
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  z-index: 1300;
  padding: 0.7rem 0.95rem;
  background: rgba(11, 19, 32, 0.96);
  color: var(--text-primary);
  border: 1px solid rgba(0, 180, 216, 0.4);
  border-left: 3px solid ${accent};
  border-radius: 6px;
  font-family: ${mono};
  font-size: 0.72rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  box-shadow: 0 14px 30px rgba(0, 0, 0, 0.4);
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  animation: arc-toast-in 0.18s ease;

  @keyframes arc-toast-in {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const AuthorList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 0.6rem;
`;

const AuthorCard = styled.article`
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 6px;
  padding: 0.85rem 0.95rem;

  h5 {
    margin: 0 0 0.35rem;
    color: var(--text-primary);
    font-size: 0.95rem;
  }

  .meta {
    font-family: ${mono};
    font-size: 0.66rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--text-disabled);
    margin-bottom: 0.55rem;
  }

  .stats {
    display: flex;
    gap: 0.6rem;
    flex-wrap: wrap;
  }
`;

/* ---------- data ---------- */

const TABS = [
  { id: 'library', label: 'Biblioteca' },
  { id: 'charts', label: 'Tendencias' },
  { id: 'insights', label: 'Insights IA' },
  { id: 'authors', label: 'Autores' },
];

const CATEGORY_FILTERS = [
  { id: 'all', label: 'Todas las categorías' },
  ...Object.entries(CATEGORY_META).map(([id, m]) => ({ id, label: m.label })),
];

const TYPE_FILTERS = [
  { id: 'all', label: 'Todos los tipos' },
  { id: 'Informe', label: 'Informes' },
  { id: 'Estudio', label: 'Estudios' },
  { id: 'Dashboard', label: 'Dashboards' },
  { id: 'Encuesta', label: 'Encuestas' },
];

const SAVED_KEY = 'arcana_research_saved_v1';
function loadSaved() {
  try { return new Set(JSON.parse(localStorage.getItem(SAVED_KEY) || '[]')); } catch { return new Set(); }
}
function persistSaved(set) {
  try { localStorage.setItem(SAVED_KEY, JSON.stringify([...set])); } catch {}
}

const fmtDate = (iso) => {
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' });
};

/* ---------- componente ---------- */

const Research = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('library');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [type, setType] = useState('all');
  const [saved, setSaved] = useState(loadSaved);
  const [viewing, setViewing] = useState(null);
  const [openDownload, setOpenDownload] = useState(null);
  const [toast, setToast] = useState(null);
  const [custom, setCustom] = useState(loadCustom);
  const [editor, setEditor] = useState({ open: false, item: null });
  const [autoData, setAutoData] = useState({ items: [], runs: [], schedule: null, loading: true });

  React.useEffect(() => {
    let cancelled = false;
    listResearch(50)
      .then((data) => {
        if (cancelled) return;
        const items = (data.items || []).map((it) => ({
          ...it,
          id: `auto-${it.id}`,
          date: (it.generated_at || '').slice(0, 10),
          tags: Array.isArray(it.tags) ? it.tags : [],
          _auto: true,
        }));
        setAutoData({ items, runs: data.runs || [], schedule: data.schedule, loading: false });
      })
      .catch(() => {
        if (!cancelled) setAutoData((s) => ({ ...s, loading: false }));
      });
    return () => { cancelled = true; };
  }, []);

  const allItems = useMemo(
    () => [...autoData.items, ...custom, ...RESEARCH_ITEMS],
    [autoData.items, custom]
  );

  const handleSaveItem = (payload) => {
    if (payload.id && custom.some((c) => c.id === payload.id)) {
      const next = custom.map((c) => (c.id === payload.id ? { ...c, ...payload } : c));
      setCustom(next);
      persistCustom(next);
      showToast('Investigación actualizada');
    } else {
      const newItem = {
        ...payload,
        id: payload.id || `c-${nextId(allItems)}`,
        _custom: true,
      };
      const next = [newItem, ...custom];
      setCustom(next);
      persistCustom(next);
      showToast('Investigación creada');
    }
    setEditor({ open: false, item: null });
  };

  const handleDeleteItem = (id) => {
    const next = custom.filter((c) => c.id !== id);
    setCustom(next);
    persistCustom(next);
    setEditor({ open: false, item: null });
    if (viewing?.id === id) setViewing(null);
    showToast('Investigación eliminada');
  };

  const showToast = (msg) => {
    setToast(msg);
    clearTimeout(showToast._t);
    showToast._t = setTimeout(() => setToast(null), 2400);
  };

  const toggleSave = (id) => {
    setSaved((prev) => {
      const next = new Set(prev);
      const wasSaved = next.has(id);
      if (wasSaved) next.delete(id);
      else next.add(id);
      persistSaved(next);
      showToast(wasSaved ? 'Quitado de guardados' : 'Guardado · accesible en sesión');
      return next;
    });
  };

  // cerrar dropdown al hacer click afuera
  React.useEffect(() => {
    if (!openDownload) return;
    const handler = () => setOpenDownload(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openDownload]);

  const handleDownload = (item, format, e) => {
    e?.stopPropagation();
    if (format === 'md') downloadMarkdown(item);
    else if (format === 'json') downloadJSON(item);
    else if (format === 'pdf') printItem(item);
    setOpenDownload(null);
    showToast(`Descargando · ${format.toUpperCase()}`);
  };

  const filtered = useMemo(() => {
    return allItems
      .filter((it) => category === 'all' || it.category === category)
      .filter((it) => type === 'all' || it.type === type)
      .filter((it) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
          it.title.toLowerCase().includes(q)
          || (it.excerpt || '').toLowerCase().includes(q)
          || (it.author || '').toLowerCase().includes(q)
          || (it.tags || []).some((t) => t.toLowerCase().includes(q))
        );
      })
      .sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [allItems, search, category, type]);

  const authors = useMemo(() => {
    const map = new Map();
    for (const it of allItems) {
      const cur = map.get(it.author) || { name: it.author, count: 0, last: null, categories: new Set() };
      cur.count += 1;
      cur.categories.add(it.category);
      if (!cur.last || it.date > cur.last) cur.last = it.date;
      map.set(it.author, cur);
    }
    return [...map.values()].sort((a, b) => b.count - a.count);
  }, [allItems]);

  return (
    <TechShell>
      <TechPageHeader
        title="Investigación"
        subtitle="Repositorio de investigaciones, red de contactos y análisis IA del cuerpo de research"
        dossier="RES://LIB/01"
      >
        <StatusPill>Estudios <strong>{allItems.length}</strong></StatusPill>
        {autoData.items.length > 0 && (
          <StatusPill>IA semanal <strong>{autoData.items.length}</strong></StatusPill>
        )}
        {custom.length > 0 && <StatusPill>Propios <strong>{custom.length}</strong></StatusPill>}
        <StatusPill>Autores <strong>{authors.length}</strong></StatusPill>
        <StatusPill>Guardados <strong>{saved.size}</strong></StatusPill>
        <CronCountdown cronKey="weekly-research" label="Próxima IA en" />
      </TechPageHeader>

      <TechTabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

      {activeTab === 'library' && (
        <>
          <FilterBar>
            <SearchWrap>
              <FaSearch />
              <TechInput
                placeholder="Buscar investigación, autor o tag…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </SearchWrap>
            <FilterGroup>
              <label><FaThList /> Categoría</label>
              <TechSelect value={category} onChange={(e) => setCategory(e.target.value)}>
                {CATEGORY_FILTERS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </TechSelect>
            </FilterGroup>
            <FilterGroup>
              <label><FaFileAlt /> Tipo</label>
              <TechSelect value={type} onChange={(e) => setType(e.target.value)}>
                {TYPE_FILTERS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </TechSelect>
            </FilterGroup>
            <TechButton type="button" onClick={() => setEditor({ open: true, item: null })}>
              <FaPlus /> Nueva investigación
            </TechButton>
          </FilterBar>

          <TechSection
            title={`Resultados · ${filtered.length}`}
            action={<TechButtonGhost type="button">Exportar índice</TechButtonGhost>}
          >
            {filtered.length === 0 ? (
              <p style={{ color: 'var(--text-disabled)', margin: 0 }}>Sin resultados.</p>
            ) : (
              <Grid>
                {filtered.map((it) => {
                  const cat = CATEGORY_META[it.category] || { color: accent, label: it.category };
                  const isSaved = saved.has(it.id);
                  return (
                    <Card key={it.id} accentColor={cat.color}>
                      <CardHead>
                        <CardTitle>{it.title}</CardTitle>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {it._auto && <TechChip>IA · semanal</TechChip>}
                          {it._custom && <TechChip tone="success">Propio</TechChip>}
                          <TechChip tone="mute">{it.type}</TechChip>
                        </div>
                      </CardHead>
                      <CardBody>
                        <CardText>{it.excerpt}</CardText>
                        <CardMeta>
                          <span>{fmtDate(it.date)}</span>
                          <span>{it.author}</span>
                        </CardMeta>
                        <TagRow>
                          <span style={{
                            fontFamily: mono,
                            fontSize: '0.62rem',
                            letterSpacing: '0.18em',
                            textTransform: 'uppercase',
                            padding: '1px 6px',
                            color: cat.color,
                            background: 'rgba(0, 180, 216, 0.04)',
                            border: `1px solid ${cat.color}55`,
                            borderRadius: 3,
                          }}>{cat.label}</span>
                          {(it.tags || []).map((t) => <Tag key={t}>{t}</Tag>)}
                        </TagRow>
                      </CardBody>
                      <CardActions>
                        <ActionBtn type="button" onClick={() => navigate(`/research/${encodeURIComponent(it.id)}`, { state: { item: it } })}>
                          <FaFileAlt /> Ver
                        </ActionBtn>

                        <DownloadWrap onClick={(e) => e.stopPropagation()}>
                          <ActionBtn
                            type="button"
                            style={{ width: '100%', borderRight: 'none' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDownload(openDownload === it.id ? null : it.id);
                            }}
                          >
                            <FaDownload /> Descargar
                          </ActionBtn>
                          {openDownload === it.id && (
                            <DownloadMenu>
                              <button type="button" onClick={(e) => handleDownload(it, 'md', e)}>
                                <FaFileAlt /> Markdown (.md)
                              </button>
                              <button type="button" onClick={(e) => handleDownload(it, 'json', e)}>
                                <FaFileCode /> JSON (.json)
                              </button>
                              <button type="button" onClick={(e) => handleDownload(it, 'pdf', e)}>
                                <FaPrint /> Imprimir / PDF
                              </button>
                            </DownloadMenu>
                          )}
                        </DownloadWrap>

                        <ActionBtn
                          type="button"
                          className={isSaved ? 'active' : ''}
                          onClick={() => toggleSave(it.id)}
                        >
                          {isSaved ? <FaBookmark /> : <FaRegBookmark />}
                          {isSaved ? 'Guardado' : 'Guardar'}
                        </ActionBtn>
                        {it._custom && (
                          <ActionBtn type="button" onClick={() => setEditor({ open: true, item: it })}>
                            <FaEdit /> Editar
                          </ActionBtn>
                        )}
                      </CardActions>
                    </Card>
                  );
                })}
              </Grid>
            )}
          </TechSection>
        </>
      )}

      {activeTab === 'charts' && (
        <>
          <FilterBar style={{ marginBottom: '1rem' }}>
            <span style={{
              fontFamily: mono,
              fontSize: '0.7rem',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'var(--text-disabled)',
            }}>
              <FaChartBar style={{ color: accent, marginRight: 6 }} />
              Producción, distribución y top autores
            </span>
          </FilterBar>
          <ResearchCharts items={allItems} onOpenItem={(it) => setViewing(it)} />
        </>
      )}

      {activeTab === 'insights' && (
        <>
          <FilterBar style={{ marginBottom: '1rem' }}>
            <span style={{
              fontFamily: mono,
              fontSize: '0.7rem',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'var(--text-disabled)',
            }}>
              <FaBrain style={{ color: accent, marginRight: 6 }} />
              Síntesis y brechas detectadas por IA
            </span>
          </FilterBar>
          <ResearchInsights />
        </>
      )}

      {viewing && (
        <ResearchViewer
          item={viewing}
          saved={saved.has(viewing.id)}
          onClose={() => setViewing(null)}
          onToggleSave={(id) => toggleSave(id)}
        />
      )}

      <ResearchEditor
        open={editor.open}
        item={editor.item}
        onClose={() => setEditor({ open: false, item: null })}
        onSave={handleSaveItem}
        onDelete={editor.item?._custom ? handleDeleteItem : undefined}
      />

      {toast && <Toast><FaCheck style={{ color: 'var(--alert-success)' }} /> {toast}</Toast>}

      {activeTab === 'authors' && (
        <>
          <FilterBar style={{ marginBottom: '1rem' }}>
            <span style={{
              fontFamily: mono,
              fontSize: '0.7rem',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'var(--text-disabled)',
            }}>
              <FaUsers style={{ color: accent, marginRight: 6 }} />
              Equipos productores de research
            </span>
          </FilterBar>
          <TechSection title={`Autores · ${authors.length}`}>
            <AuthorList>
              {authors.map((a) => (
                <AuthorCard key={a.name}>
                  <h5>{a.name}</h5>
                  <div className="meta">Última publicación · {fmtDate(a.last)}</div>
                  <div className="stats">
                    <TechChip tone="mute">{a.count} estudios</TechChip>
                    <TechChip tone="mute">{a.categories.size} áreas</TechChip>
                  </div>
                </AuthorCard>
              ))}
            </AuthorList>
          </TechSection>
        </>
      )}
    </TechShell>
  );
};

export default Research;
